import React, { useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useLocation } from 'react-router-dom';
import AuditPage from './pages/AuditPage';
import GeneratePage from './pages/GeneratePage';
import ArchitecturePage from './pages/ArchitecturePage';
import { ChartBarIcon, PencilIcon, HomeIcon, CpuChipIcon, LightBulbIcon, CheckBadgeIcon } from '@heroicons/react/24/outline';

function Navigation() {
  const location = useLocation();
  
  const navigation = [
    { name: 'Home', href: '/', icon: HomeIcon, current: location.pathname === '/' },
    { name: 'Website Audit', href: '/audit', icon: ChartBarIcon, current: location.pathname === '/audit' },
    { name: 'Content Generator', href: '/generate', icon: PencilIcon, current: location.pathname === '/generate' },
    { name: 'Architecture', href: '/architecture', icon: CpuChipIcon, current: location.pathname === '/architecture' },
  ];

  return (
    <nav className="bg-white shadow">
      <div className="relative w-full">
        <div className="flex items-center justify-between h-20 px-4 sm:px-6 lg:px-8">
          <div className="flex items-center" style={{ position: 'absolute', left: '3rem' }}>
            <Link to="/" className="flex items-center hover:opacity-80 transition-opacity">
              <img 
                src="/figtree_logo.png" 
                alt="Figtree" 
                className="h-15 w-auto cursor-pointer"
                style={{ height: '60px' }}
              />
            </Link>
          </div>
          <div className="flex items-center ml-auto">
            <div className="hidden sm:flex sm:space-x-8">
            {navigation.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                className={`${
                  item.current
                    ? 'text-gray-900'
                    : 'text-gray-500 hover:text-gray-700'
                } inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium transition-colors`}
                style={{
                  borderBottomColor: item.current ? '#6CB955' : 'transparent'
                }}
                onMouseEnter={(e) => {
                  if (!item.current) {
                    e.target.style.borderBottomColor = '#4A8B87';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!item.current) {
                    e.target.style.borderBottomColor = 'transparent';
                  }
                }}
              >
                <item.icon className="w-5 h-5 mr-1" />
                {item.name}
              </Link>
            ))}
            </div>
          </div>
        </div>
      </div>
    </nav>
  );
}

function HomePage() {
  return (
    <div className="min-h-screen bg-gray-50">
      <div className="relative overflow-hidden">
        <div className="gradient-bg" style={{ background: 'linear-gradient(135deg, #2C5F5D 0%, #4A8B87 50%, #6CB955 100%)' }}>
          <div className="max-w-7xl mx-auto">
            <div className="relative z-10 pb-8 sm:pb-16 md:pb-20 lg:max-w-2xl lg:w-full lg:pb-28 xl:pb-32">
              <main className="pt-10 mx-auto max-w-7xl px-4 sm:pt-12 sm:px-6 md:pt-16 lg:pt-20 lg:px-8 xl:pt-28">
                <div className="sm:text-center lg:text-left">
                  <h1 className="text-4xl tracking-tight font-extrabold text-white sm:text-5xl md:text-6xl">
                    <span className="block xl:inline">AI-Powered Content for</span>{' '}
                    <span className="block xl:inline" style={{ color: '#6CB955' }}>Tourism & Hospitality</span>
                  </h1>
                  <p className="mt-3 text-base sm:mt-5 sm:text-lg sm:max-w-xl sm:mx-auto md:mt-5 md:text-xl lg:mx-0" style={{ color: '#ffffff' }}>
                    Transform your tourism and hospitality business with Multi-agentic powered content generation. 
                    Our system audits your website, develops strategic content plans, and creates 
                    compelling copy that converts visitors into bookings.
                  </p>
                  <div className="mt-5 sm:mt-8 sm:flex sm:justify-center lg:justify-start">
                    <div className="rounded-md shadow">
                      <Link
                        to="/audit"
                        className="w-full flex items-center justify-center px-8 py-3 border border-transparent text-base font-medium rounded-md bg-white hover:bg-gray-50 md:py-4 md:text-lg md:px-10 transition-colors"
                        style={{ color: '#2C5F5D' }}
                      >
                        Start Website Audit
                      </Link>
                    </div>
                    <div className="mt-3 sm:mt-0 sm:ml-3">
                      <Link
                        to="/generate"
                        className="w-full flex items-center justify-center px-8 py-3 border border-transparent text-base font-medium rounded-md text-white md:py-4 md:text-lg md:px-10 transition-colors"
                        style={{ backgroundColor: '#6CB955' }}
                        onMouseEnter={(e) => e.target.style.backgroundColor = '#4A9B3A'}
                        onMouseLeave={(e) => e.target.style.backgroundColor = '#6CB955'}
                      >
                        Generate Content
                      </Link>
                    </div>
                  </div>
                </div>
              </main>
            </div>
          </div>
        </div>
      </div>

      {/* Features Section */}
      <div className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="lg:text-center">
            <h2 className="text-base font-semibold tracking-wide uppercase" style={{ color: '#2C5F5D' }}>Features</h2>
            <p className="mt-2 text-3xl leading-8 font-extrabold tracking-tight text-gray-900 sm:text-4xl">
              Four-Agent Intelligence System
            </p>
            <p className="mt-4 max-w-2xl text-xl text-gray-500 lg:mx-auto">
              Our specialised AI agents work together to deliver exceptional results.
            </p>
          </div>

          <div className="mt-10">
            <dl className="space-y-10 md:space-y-0 md:grid md:grid-cols-2 md:gap-x-8 md:gap-y-10">
              <div className="relative">
                <dt>
                  <div className="absolute flex items-center justify-center h-12 w-12 rounded-md text-white" style={{ backgroundColor: '#6CB955' }}>
                    <ChartBarIcon className="h-6 w-6" aria-hidden="true" />
                  </div>
                  <p className="ml-16 text-lg leading-6 font-medium text-gray-900">Auditor Agent</p>
                </dt>
                <dd className="mt-2 ml-16 text-base text-gray-500">
                  Comprehensively analyses your website to extract brand information, amenities, target segments, 
                  and competitive positioning insights.
                </dd>
              </div>

              <div className="relative">
                <dt>
                  <div className="absolute flex items-center justify-center h-12 w-12 rounded-md text-white" style={{ backgroundColor: '#4A8B87' }}>
                    <LightBulbIcon className="h-6 w-6" aria-hidden="true" />
                  </div>
                  <p className="ml-16 text-lg leading-6 font-medium text-gray-900">Strategy Agent</p>
                </dt>
                <dd className="mt-2 ml-16 text-base text-gray-500">
                  Develops market positioning, competitive strategy, and content frameworks based on audit findings 
                  and industry expertise.
                </dd>
              </div>

              <div className="relative">
                <dt>
                  <div className="absolute flex items-center justify-center h-12 w-12 rounded-md text-white" style={{ backgroundColor: '#2C5F5D' }}>
                    <PencilIcon className="h-6 w-6" aria-hidden="true" />
                  </div>
                  <p className="ml-16 text-lg leading-6 font-medium text-gray-900">Content Agent</p>
                </dt>
                <dd className="mt-2 ml-16 text-base text-gray-500">
                  Creates compelling, conversion-focused content following strategic guidelines and 
                  Australian/NZ market best practices.
                </dd>
              </div>

              <div className="relative">
                <dt>
                  <div className="absolute flex items-center justify-center h-12 w-12 rounded-md text-white" style={{ backgroundColor: '#4A9B3A' }}>
                    <CheckBadgeIcon className="h-6 w-6" aria-hidden="true" />
                  </div>
                  <p className="ml-16 text-lg leading-6 font-medium text-gray-900">Quality Agent</p>
                </dt>
                <dd className="mt-2 ml-16 text-base text-gray-500">
                  Ensures content meets the highest standards through SEO optimisation, readability analysis, 
                  and quality assurance checks.
                </dd>
              </div>
            </dl>
          </div>
        </div>
      </div>
    </div>
  );
}

function App() {
  return (
    <Router>
      <div className="min-h-screen bg-gray-50">
        <Navigation />
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/audit" element={<AuditPage />} />
          <Route path="/generate" element={<GeneratePage />} />
          <Route path="/architecture" element={<ArchitecturePage />} />
        </Routes>
      </div>
    </Router>
  );
}

export default App;